// client/src/components/admin/advertiser/AdvertiserPointDeductModal.js
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdvertiserPointDeductModal({ open, onClose, advertiser, onSuccess }) {
  const [points, setPoints] = useState('');
  const [memo, setMemo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPoints('');
      setMemo('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!advertiser) return;

    setError('');

    const pointAmount = Number(points);
    if (!points || isNaN(pointAmount) || pointAmount <= 0) {
      setError('차감할 포인트를 입력해주세요. (양수만 가능)');
      return;
    }

    const currentPoints = advertiser.points || 0;
    if (pointAmount > currentPoints) {
      setError('보유 포인트보다 많이 차감할 수 없습니다.');
      return;
    }

    // 환불 처리 중인 포인트 확인
    if (advertiser.pendingRefundPoints > 0) {
      setError('환불 처리 중인 포인트가 있어 포인트를 조정할 수 없습니다.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${API_URL}/api/admin/advertiser/info/${advertiser.userId}/points/adjust`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            amount: -pointAmount, // 음수
            memo: memo.trim() || undefined
          })
        }
      );

      const data = await res.json();

      if (data.ok) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setError(data.message || '포인트 차감에 실패했습니다.');
      }
    } catch (err) {
      console.error('handleSubmit error:', err);
      setError('포인트 차감 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !advertiser) return null;

  const currentPoints = advertiser.points || 0;
  const pendingRefundPoints = advertiser.pendingRefundPoints || 0;
  const pointAmount = Number(points) || 0;
  const expectedPoints = currentPoints - pointAmount;

  // 환불 처리 중인 포인트가 있으면 경고
  const hasPendingRefund = pendingRefundPoints > 0;

  return (
    <div className="advertiser-point-modal" onClick={onClose}>
      <div className="advertiser-point-modal__content" onClick={(e) => e.stopPropagation()}>
        <h3 className="advertiser-point-modal__title advertiser-point-modal__title--deduct">포인트 차감</h3>

        {/* 환불 처리 중 경고 */}
        {hasPendingRefund && (
          <div className="advertiser-point-modal__warning">
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              ⚠️ 환불 처리 중인 포인트가 있습니다
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
              환불 처리 중: {pendingRefundPoints.toLocaleString()}pt
            </div>
            <div style={{ fontSize: 13, color: '#e65100' }}>
              환불 처리가 완료될 때까지 포인트 차감이 불가능합니다.
            </div>
          </div>
        )}

        {/* 현재 포인트 표시 */}
        <div className="advertiser-point-modal__info">
          <div className="advertiser-point-modal__info-row">
            <span className="advertiser-point-modal__info-label">현재 보유 포인트:</span>
            <span className="advertiser-point-modal__info-value">{currentPoints.toLocaleString()}pt</span>
          </div>
          {pendingRefundPoints > 0 && (
            <div className="advertiser-point-modal__info-row" style={{ color: '#f57c00' }}>
              <span>환불 처리 중:</span>
              <strong>{pendingRefundPoints.toLocaleString()}pt</strong>
            </div>
          )}
        </div>

        {/* 차감 포인트 입력 */}
        <div className="advertiser-point-modal__form-group">
          <label className="advertiser-point-modal__label">
            차감할 포인트 <span style={{ color: '#d32f2f' }}>*</span>
          </label>
          <input
            type="number"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="차감할 포인트를 입력하세요"
            min="1"
            max={currentPoints}
            disabled={hasPendingRefund}
            className="advertiser-point-modal__input"
            style={{ background: hasPendingRefund ? '#f5f5f5' : '#fff' }}
          />
        </div>

        {/* 예상 포인트 표시 */}
        {pointAmount > 0 && !hasPendingRefund && (
          <div 
            className="advertiser-point-modal__preview" 
            style={{ 
              background: pointAmount > currentPoints ? '#ffebee' : '#fff3e0',
              border: `1px solid ${pointAmount > currentPoints ? '#c62828' : '#f57c00'}`
            }}
          >
            <div className="advertiser-point-modal__preview-label">
              차감 후 예상 포인트
            </div>
            <div 
              className="advertiser-point-modal__preview-value"
              style={{ color: pointAmount > currentPoints ? '#c62828' : '#f57c00' }}
            >
              {expectedPoints.toLocaleString()}pt
              <span style={{ fontSize: 14, fontWeight: 400, color: '#666', marginLeft: 8 }}>
                (-{pointAmount.toLocaleString()}pt)
              </span>
            </div>
            {pointAmount > currentPoints && (
              <div style={{ fontSize: 13, color: '#c62828', marginTop: 8 }}>
                ⚠️ 보유 포인트가 부족합니다
              </div>
            )}
          </div>
        )}

        {/* 사유 입력 (선택) */}
        <div className="advertiser-point-modal__form-group">
          <label className="advertiser-point-modal__label">
            사유 (선택)
          </label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="포인트 차감 사유를 입력하세요 (선택사항)"
            rows={3}
            disabled={hasPendingRefund}
            className="advertiser-point-modal__textarea"
            style={{ background: hasPendingRefund ? '#f5f5f5' : '#fff' }}
          />
        </div>

        {error && (
          <div className="advertiser-point-modal__error">
            {error}
          </div>
        )}

        <div className="advertiser-point-modal__actions">
          <button
            onClick={onClose}
            disabled={loading}
            className="advertiser-point-modal__button advertiser-point-modal__button--cancel"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || hasPendingRefund || pointAmount > currentPoints}
            className="advertiser-point-modal__button advertiser-point-modal__button--deduct"
          >
            {loading ? '처리 중...' : '차감'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdvertiserPointDeductModal;

